/**
 * Moz Safe-Fetch Wrapper (Story 20.7, AC #5; Round 4 Red Team Attack #1; AC #8 30-sec cooldown)
 *
 * Thin shim over the existing `safeFetch`. The shim's job is THREE-fold:
 *
 *  1. Inject the `x-moz-token: <decryptedToken>` header (Moz V3 auth shape per
 *     moz.com/api/docs/guides/authentication verified 2026-05-10) on every
 *     request. The token is held in scope for the lifetime of the call AND
 *     passed to `sanitizeMozErrorMessage` for redaction on the error path.
 *  2. Redact the literal `x-moz-token: <value>` substring from the error
 *     message before any structured-log emission, Sentry breadcrumb, or
 *     adapter-error surface (Round 4 Red Team Attack #1).
 *  3. Track 4xx error counts in a process-local LRU map keyed by integrationId
 *     and enforce the **30-second cooldown after 50+ recent 4xx responses in
 *     a 5-min sliding window** per AC #8. This is a Moz-specific divergence
 *     from Ahrefs/Semrush retry semantics — Moz documents at
 *     moz.com/api/docs/guides/error-handling that "restriction lifts after
 *     30 seconds without 4xx responses" so re-firing on 4xx immediately
 *     prolongs the lockout.
 *
 * **Why this is necessary:** Moz V3 differs from Semrush/Ahrefs in that the
 * token appears in EVERY request HEADER (not URL/query). Without explicit
 * sanitization at the boundary, the token can leak into:
 *
 *   - Cloudflare Workers' built-in request logs (URL is fine; headers are
 *     usually NOT logged by default but Sentry/structured-log integrations
 *     often include request context).
 *   - Sentry breadcrumbs / error-tracking stacks.
 *   - Our own structured-log events (`moz_api_call_*`).
 *   - Customer-visible adapter error messages on a 5xx surface (where the
 *     upstream provider may echo the request headers in the body).
 *
 * Defense at the boundary > defense at every consumer (which we'd inevitably
 * miss in some code path).
 */

import { safeFetch } from "../safe-fetch";
import type { AdapterResult } from "../adapter";
import { sanitizeMozErrorMessage } from "./errors";

/** Per-call timeout (15s — matches Ahrefs + Semrush). */
const MOZ_TIMEOUT_MS = 15_000;

/** Moz-specific 30-sec cooldown trigger threshold (AC #8). */
export const MOZ_COOLDOWN_4XX_THRESHOLD = 50;

/** Moz-specific 5-min sliding window for the 4xx counter (AC #8). */
export const MOZ_COOLDOWN_WINDOW_MS = 5 * 60 * 1000;

/** Moz-specific cooldown duration enforced after threshold breach (AC #8). */
export const MOZ_COOLDOWN_DURATION_MS = 30 * 1000;

// ============================================================================
// Process-local 4xx tracker (AC #8)
// ============================================================================

/**
 * In-process LRU map of recent 4xx timestamps per integrationId. NOT a KV
 * store — this is purely transient request-throttling within a single Worker
 * invocation. Multi-Worker coordination is unnecessary because each Worker
 * sees its own local 4xx history; the cooperative wait is per-Worker, and the
 * aggregate effect across all workers is the desired thundering-herd
 * suppression.
 *
 * Limited to 256 integrations to bound memory; oldest entries evicted via
 * Map's insertion-order iteration when capacity is breached.
 */
const FOUR_XX_TIMESTAMPS = new Map<string, number[]>();
const MAX_TRACKED_INTEGRATIONS = 256;

/** Current cooldown-until timestamp per integrationId (AC #8). */
const COOLDOWN_UNTIL = new Map<string, number>();

function pruneOldTimestamps(timestamps: number[], now: number): number[] {
  const cutoff = now - MOZ_COOLDOWN_WINDOW_MS;
  let i = 0;
  while (i < timestamps.length && timestamps[i]! < cutoff) {
    i++;
  }
  return i === 0 ? timestamps : timestamps.slice(i);
}

/**
 * Record a 4xx response for the given integration. If the count within the
 * 5-min window crosses `MOZ_COOLDOWN_4XX_THRESHOLD`, set the cooldown-until
 * timestamp 30 sec into the future.
 *
 * Exported for tests; production callers go through `safeFetchMoz`.
 */
export function record4xxForIntegration(integrationId: string, now: number = Date.now()): void {
  let timestamps = FOUR_XX_TIMESTAMPS.get(integrationId) ?? [];
  timestamps = pruneOldTimestamps(timestamps, now);
  timestamps.push(now);
  FOUR_XX_TIMESTAMPS.set(integrationId, timestamps);

  if (timestamps.length >= MOZ_COOLDOWN_4XX_THRESHOLD) {
    COOLDOWN_UNTIL.set(integrationId, now + MOZ_COOLDOWN_DURATION_MS);
  }

  // Bound memory: evict the oldest tracked integration if we exceed capacity.
  if (FOUR_XX_TIMESTAMPS.size > MAX_TRACKED_INTEGRATIONS) {
    const oldestKey = FOUR_XX_TIMESTAMPS.keys().next().value;
    if (oldestKey !== undefined) {
      FOUR_XX_TIMESTAMPS.delete(oldestKey);
      COOLDOWN_UNTIL.delete(oldestKey);
    }
  }
}

/**
 * Returns the cooldown-until timestamp for `integrationId` if we are currently
 * within a cooldown window, else null.
 *
 * Exported for tests.
 */
export function getCooldownUntil(integrationId: string, now: number = Date.now()): number | null {
  const until = COOLDOWN_UNTIL.get(integrationId);
  if (until === undefined) return null;
  if (until <= now) {
    COOLDOWN_UNTIL.delete(integrationId);
    return null;
  }
  return until;
}

/**
 * Reset the in-process 4xx tracker — used by tests and by the disconnect
 * route (per AC #9 to keep the LRU clean across re-connects).
 */
export function resetMoz4xxTracker(integrationId?: string): void {
  if (integrationId === undefined) {
    FOUR_XX_TIMESTAMPS.clear();
    COOLDOWN_UNTIL.clear();
    return;
  }
  FOUR_XX_TIMESTAMPS.delete(integrationId);
  COOLDOWN_UNTIL.delete(integrationId);
}

/**
 * Round 1 review fix — clears only the 4xx-timestamp history for the given
 * integration (preserves any OTHER cooldown state, but in practice cooldown
 * was just consumed so we also clear it). Distinct from `resetMoz4xxTracker`
 * which is for housekeeping after disconnect.
 */
export function clear4xxForIntegration(integrationId: string): void {
  FOUR_XX_TIMESTAMPS.delete(integrationId);
  COOLDOWN_UNTIL.delete(integrationId);
}

// ============================================================================
// safeFetchMoz wrapper
// ============================================================================

export interface SafeFetchMozOptions {
  /** Optional integrationId to wire the per-integration 30-sec cooldown tracker. */
  readonly integrationId?: string;
  /** Optional override for the wall-clock (for test determinism). */
  readonly now?: () => number;
}

/**
 * `safeFetch` wrapper that:
 *
 *  1. Injects the `x-moz-token: <decryptedToken>` header alongside any
 *     caller-provided headers.
 *  2. Enforces the AC #8 30-sec cooldown when the per-integration 4xx counter
 *     has crossed threshold within the 5-min sliding window.
 *  3. On error, sanitizes the error message via `sanitizeMozErrorMessage`
 *     so the literal token is redacted from any surface that downstream code
 *     might log.
 *  4. On any 4xx HTTP response from Moz, records the timestamp for the
 *     per-integration cooldown counter.
 *
 * **Body sanity check (defense — Code Review Pass 3 invariant carried forward
 * from Semrush 20.4):** the request body MUST NOT contain the API token.
 * Moz V3 sends the token via header ONLY; if a future helper accidentally
 * stuffs the token into the JSON-RPC body (where it doesn't belong), this
 * synchronous TypeError catches the misuse before the request goes out.
 */
export async function safeFetchMoz(
  url: string,
  decryptedToken: string,
  init?: RequestInit,
  options: SafeFetchMozOptions = {},
): Promise<AdapterResult<Response>> {
  // Pass 3 defense — synchronous TypeError if a caller accidentally puts
  // the API token in the body. Cheap check; only trips on actual misuse.
  // **Round 1 review fix:** raised threshold to 40 chars (matches Zod schema
  // min(40)). Below 40 we skip the check — a too-short test stub would
  // false-positive on shared substrings.
  // Body shapes other than `string` (`Uint8Array`, `URLSearchParams`,
  // `ReadableStream`) are NOT inspected — the helper here ONLY emits JSON-RPC
  // POSTs with string bodies. A future helper switching to a binary body
  // would need to defend at its own boundary.
  if (
    decryptedToken.length >= 40 &&
    typeof init?.body === "string" &&
    init.body.includes(decryptedToken)
  ) {
    throw new TypeError(
      "safeFetchMoz: init.body must not contain the API token. Use the x-moz-token header only.",
    );
  }

  const now = options.now ?? Date.now;
  const integrationId = options.integrationId;

  // AC #8 cooldown check — if the per-integration 4xx counter has crossed
  // threshold within the 5-min window, sleep until the cooldown expires.
  if (integrationId !== undefined) {
    const cooldownUntil = getCooldownUntil(integrationId, now());
    if (cooldownUntil !== null) {
      const sleepMs = Math.max(0, cooldownUntil - now());
      // Cooperative wait — consumes the queue worker for `sleepMs` ms but
      // is bounded to MOZ_COOLDOWN_DURATION_MS (30 sec). Acceptable because
      // the Cloudflare Worker has a 30-sec CPU budget and a 60-sec wall-clock
      // budget per request; 30-sec sleep is the documented Moz cooldown.
      if (sleepMs > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, sleepMs));
      }
      // **Round 1 review fix (cooldown re-trip loop):** after the sleep
      // completes, prune the 4xx counter so old timestamps within the 5-min
      // window cannot re-trip the threshold immediately on the next call.
      // Without this, a burst that hit threshold once stays "above threshold"
      // for the whole 5-min window even after we paid the 30-sec penalty.
      clear4xxForIntegration(integrationId);
    }
  }

  // Inject `x-moz-token` header; preserve any caller-provided headers.
  const mergedHeaders: Record<string, string> = {
    "x-moz-token": decryptedToken,
    "Content-Type": "application/json",
  };
  if (init?.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => {
        mergedHeaders[key] = value;
      });
    } else if (Array.isArray(init.headers)) {
      for (const [key, value] of init.headers) {
        mergedHeaders[key] = value;
      }
    } else {
      Object.assign(mergedHeaders, init.headers);
    }
    // Re-overwrite with our token in case a caller tried to set it.
    mergedHeaders["x-moz-token"] = decryptedToken;
  }

  const result = await safeFetch(
    url,
    { ...init, headers: mergedHeaders },
    MOZ_TIMEOUT_MS,
  );

  if (result.success) {
    // Track 4xx for AC #8 cooldown.
    if (integrationId !== undefined) {
      const status = result.data.status;
      if (status >= 400 && status < 500) {
        record4xxForIntegration(integrationId, now());
      }
    }
    return result;
  }

  // Error path — re-sanitize via Moz-specific helper.
  return {
    success: false,
    error: {
      ...result.error,
      message: sanitizeMozErrorMessage(result.error.message, decryptedToken),
      providerMessage: result.error.providerMessage
        ? sanitizeMozErrorMessage(result.error.providerMessage, decryptedToken)
        : undefined,
    },
  };
}
